// src/pages/ReportsStudents.tsx
import {
  Fragment,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { printPage } from "../utils/printPage";
import { LdsBadge } from "../components/lds/LdsBadge";
import { useTranslation } from "react-i18next";
import {
  FaCalendarAlt,
  FaPrint,
  FaPhone,
  FaUsers,
} from "react-icons/fa";
import Spinner from "../components/Spinner";
import SchoolYearDropdown from "../components/SchoolYearDropdown";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import http from "../api/apiClient";
import { AuthContext } from "../AuthProvider";
import { useCan } from "../authz/useCan";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { translateClassName } from "../utils/classroomUtils";
import { parseToDate } from "../utils/dateUtils";

type EntityId = string | number;
type PrintMode = "per-class" | "compact";

interface Person {
  id?: EntityId;
  uuid?: EntityId;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  phone?: string;
  phoneNumber?: string;
  mobile?: string;
  address?: string | null;
  zipCode?: string | null;
  city?: string | null;
}

interface Student extends Person {
  parents?: Person[];
  parent1?: Person | null;
  parent2?: Person | null;
}

interface SchoolYear {
  id?: EntityId;
  uuid?: EntityId;
  label?: string;
  name?: string;
  yearRange?: string;
  display?: string;
  description?: string;
  code?: string;
  title?: string;
  schoolYearLabel?: string;
  school_year_label?: string;
  startDate?: unknown;
  start_date?: unknown;
  start?: unknown;
  from?: unknown;
  startYear?: unknown;
  start_year?: unknown;
  yearStart?: unknown;
  endDate?: unknown;
  end_date?: unknown;
  end?: unknown;
  to?: unknown;
  endYear?: unknown;
  end_year?: unknown;
  yearEnd?: unknown;
  year?: unknown;
  yearCode?: unknown;
  schoolYear?: SchoolYear;
  __rawId?: EntityId | null;
  __label?: string;
  isDivider?: boolean;
}

interface ClassList {
  id?: EntityId;
  classroomId?: EntityId;
  className?: string;
  name?: string;
  title?: string;
  teacher?: string;
  children?: Student[] | ApiList<Student>;
}

interface ApiList<T> {
  content?: T[];
  items?: T[];
  data?: T[];
}

interface ReportError {
  message: string;
  status: number;
}

interface StudentTableRow {
  id: string;
  child: Student;
  parent1: Person | null;
  parent2: Person | null;
  addresses: { child: string; parent1: string; parent2: string };
}

const normalizeList = <T,>(value: T[] | ApiList<T> | null | undefined): T[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const getCurrentSchoolYearLabel = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const startYear = now.getMonth() + 1 >= 8 ? year : year - 1;
  return `${startYear}/${startYear + 1}`;
};

const getYearNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getFullYear();
  }
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = parseToDate(value);
  if (parsed) return parsed.getFullYear();

  const match = String(value).match(/\b(19|20)\d{2}\b/);
  if (!match?.[0]) return null;

  const numeric = Number.parseInt(match[0], 10);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildYearLabel = (year: SchoolYear | null | undefined): string => {
  if (!year) return "—";

  const pickText = (value: unknown): string => {
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    return "";
  };

  const explicit = [
    year.label,
    year.name,
    year.yearRange,
    year.display,
    year.description,
    year.code,
    year.title,
    year.schoolYearLabel,
    year.school_year_label,
  ]
    .map(pickText)
    .find(Boolean);

  if (explicit) return explicit;

  const startYear = getYearNumber(
    year.startDate ??
      year.start_date ??
      year.start ??
      year.from ??
      year.startYear ??
      year.start_year ??
      year.yearStart ??
      year.schoolYear?.startDate ??
      year.schoolYear?.start_year ??
      year.schoolYear?.startYear
  );

  const endYear = getYearNumber(
    year.endDate ??
      year.end_date ??
      year.end ??
      year.to ??
      year.endYear ??
      year.end_year ??
      year.yearEnd ??
      year.schoolYear?.endDate ??
      year.schoolYear?.end_year ??
      year.schoolYear?.endYear
  );

  if (startYear !== null && endYear !== null) return `${startYear}/${endYear}`;
  if (startYear !== null) return `${startYear}/${startYear + 1}`;
  if (endYear !== null) return `${endYear - 1}/${endYear}`;

  const numericYear = getYearNumber(year.year ?? year.yearCode ?? year.id);
  return numericYear !== null ? `${numericYear}/${numericYear + 1}` : "—";
};

const normalizeYearLabel = (value: unknown): string =>
  value ? String(value).replace(/[^0-9]/g, "") : "";

const byStartDateDesc = (a: SchoolYear, b: SchoolYear): number => {
  const aYear = getYearNumber(a.startDate ?? a.start_date ?? a.start) ?? 0;
  const bYear = getYearNumber(b.startDate ?? b.start_date ?? b.start) ?? 0;
  return bYear - aYear;
};

const extractNameParts = (
  person: Person | null | undefined
): { first: string; last: string } => ({
  first: person?.firstName ?? person?.first_name ?? "",
  last: person?.lastName ?? person?.last_name ?? "",
});

const fullName = (person: Person | null | undefined): string => {
  const { first, last } = extractNameParts(person);
  return [first, last].filter(Boolean).join(" ");
};

const renderName = (person: Person | null | undefined): ReactNode => {
  const { first, last } = extractNameParts(person);
  if (!first && !last) return "—";

  return (
    <>
      {first ? <span className="font-semibold">{first}</span> : null}
      {first && last ? " " : null}
      {last}
    </>
  );
};

const phoneOf = (person: Person | null | undefined): string =>
  person?.phone ?? person?.phoneNumber ?? person?.mobile ?? "";

const addressOf = (person: Person | null | undefined): string => {
  const street = (person?.address ?? "").trim();
  const place = [person?.zipCode, person?.city]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return [street, place].filter(Boolean).join(", ");
};

const addressKey = (address: string): string =>
  address.toLowerCase().replace(/[\s,.]/g, "");

/**
 * Print-only addresses. A child has no address of its own: it lives at the address of the
 * first parent that has one. A parent's address is only repeated when it differs from that.
 */
const printAddresses = (
  parent1: Person | null,
  parent2: Person | null
): { child: string; parent1: string; parent2: string } => {
  const address1 = addressOf(parent1);
  const address2 = addressOf(parent2);
  const child = address1 || address2;
  const ifElsewhere = (address: string): string =>
    address && addressKey(address) !== addressKey(child) ? address : "";

  return {
    child,
    parent1: ifElsewhere(address1),
    parent2: ifElsewhere(address2),
  };
};

const renderPrintAddress = (address: string): ReactNode =>
  address ? <div className="print-only print-address">{address}</div> : null;

const classroomLabel = (classroom: ClassList): string =>
  translateClassName({ className: classroom.className }) ||
  classroom.className ||
  classroom.name ||
  classroom.title ||
  "";

const classroomSortKey = (
  rawName: string
): { typeOrder: number; num: number; raw: string } => {
  if (!rawName) return { typeOrder: 99, num: 999, raw: "" };

  const name = rawName.toLowerCase().trim();
  const number = Number.parseInt(name.match(/\d+/)?.[0] ?? "0", 10);

  if (name.includes("kleuter") || name.includes("kindergarten")) {
    return { typeOrder: 0, num: number, raw: name };
  }

  if (
    name.includes("klas") ||
    name.includes("class") ||
    name.includes("groep")
  ) {
    return { typeOrder: 1, num: number, raw: name };
  }

  return { typeOrder: 2, num: 999, raw: name };
};

const extractParents = (
  child: Student
): { parent1: Person | null; parent2: Person | null } => {
  const parents = Array.isArray(child.parents)
    ? child.parents
    : [child.parent1, child.parent2].filter(
        (parent): parent is Person => Boolean(parent)
      );

  return {
    parent1: parents[0] ?? null,
    parent2: parents[1] ?? null,
  };
};

const getEntityKey = (
  entity: { id?: EntityId; uuid?: EntityId },
  fallback: string
): string => String(entity.id ?? entity.uuid ?? fallback);

/** Safari ignores the requested paper orientation (@page size), so its users choose it in the print dialog. */
export const isSafariBrowser = (userAgent: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): boolean =>
  /safari/i.test(userAgent) && !/chrome|chromium|crios|fxios|edg|opr|android/i.test(userAgent);

export default function ReportsStudents() {
  const { t, i18n } = useTranslation();
  const { authenticated } = useContext(AuthContext);
  const canView = useCan("reports.students", "view");

  const [years, setYears] = useState<SchoolYear[]>([]);
  const [yearId, setYearId] = useState<string | null>(null);
  const [classlists, setClasslists] = useState<ClassList[]>([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingLists, setLoadingLists] = useState(false);
  const [error, setError] = useState<ReportError | null>(null);
  const [printMode, setPrintMode] = useState<PrintMode>("per-class");

  const lastFetchKeyRef = useRef<string | null>(null);
  const yearsFetchKeyRef = useRef<string | null>(null);
  const yearSelectId = "classlist-year-filter";
  const currentSchoolYearLabel = getCurrentSchoolYearLabel();

  const triggerPrint = (mode: PrintMode): void => {
    // Apply the print mode synchronously and print within the click itself: Safari ignores
    // window.print() that runs later (e.g. from a timeout) because it no longer counts as a user action.
    flushSync(() => setPrintMode(mode));
    printPage();
  };

  useEffect(() => {
    const yearsFetchKey = currentSchoolYearLabel || "current";
    if (yearsFetchKeyRef.current === yearsFetchKey) return;

    yearsFetchKeyRef.current = yearsFetchKey;
    let active = true;

    const loadYears = async (): Promise<void> => {
      try {
        setLoadingYears(true);
        setError(null);

        const response = await http.get("/years");
        const raw = normalizeList<SchoolYear>(response.data);
        if (!active) return;

        const sorted = raw
          .slice()
          .sort(byStartDateDesc)
          .map((year) => ({
            ...year,
            __rawId: year.id ?? year.uuid ?? null,
            __label: buildYearLabel(year),
          }));

        const current = sorted.find(
          (year) =>
            normalizeYearLabel(year.__label) ===
            normalizeYearLabel(currentSchoolYearLabel)
        );

        const rest = current
          ? sorted.filter(
              (year) =>
                String(year.__rawId ?? year.id ?? year.uuid) !==
                String(current.__rawId ?? current.id ?? current.uuid)
            )
          : sorted;

        const combined =
          current && rest.length
            ? [
                current,
                { id: "__divider__", isDivider: true } as SchoolYear,
                ...rest,
              ]
            : sorted;

        setYears(combined);

        const initial = current ?? sorted[0] ?? null;
        const initialId =
          initial?.__rawId !== null && initial?.__rawId !== undefined
            ? String(initial.__rawId)
            : initial
            ? buildYearLabel(initial)
            : null;

        setYearId(initialId);
      } catch (caught: unknown) {
        if (!active) return;
        const mapped = mapHttpErrorToMessage(caught, t);
        const axiosLike = caught as {
          message?: string;
          response?: { status?: number };
        };

        setError({
          message: String(
            typeof mapped === "string"
              ? mapped
              : mapped?.message ??
                  mapped?.title ??
                  axiosLike.message ??
                  "Unexpected error"
          ),
          status: axiosLike.response?.status ?? 500,
        });
      } finally {
        if (active) setLoadingYears(false);
      }
    };

    void loadYears();
    return () => {
      active = false;
    };
  }, [currentSchoolYearLabel, t]);

  useEffect(() => {
    if (!yearId) return;

    const selected =
      years.find(
        (year) =>
          !year.isDivider &&
          year.__rawId !== null &&
          year.__rawId !== undefined &&
          String(year.__rawId) === yearId
      ) ??
      years.find(
        (year) => !year.isDivider && year.__label === yearId
      ) ??
      null;

    const schoolYearId = selected?.__rawId ?? yearId;
    const fetchKey = `${yearId}::${String(schoolYearId ?? "none")}`;

    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;

    let active = true;

    const loadClassLists = async (): Promise<void> => {
      try {
        setLoadingLists(true);
        setError(null);

        const response = await http.get("/reports/classlists", {
          params: schoolYearId ? { schoolYearId } : {},
        });

        const list = normalizeList<ClassList>(response.data);
        if (!active) return;

        const sorted = list.slice().sort((a, b) => {
          const aKey = classroomSortKey(classroomLabel(a));
          const bKey = classroomSortKey(classroomLabel(b));

          if (aKey.typeOrder !== bKey.typeOrder) {
            return aKey.typeOrder - bKey.typeOrder;
          }
          if (aKey.num !== bKey.num) return aKey.num - bKey.num;

          return aKey.raw.localeCompare(
            bKey.raw,
            i18n.language || "en",
            { numeric: true, sensitivity: "base" }
          );
        });

        setClasslists(sorted);
      } catch (caught: unknown) {
        if (!active) return;
        const mapped = mapHttpErrorToMessage(caught, t);
        const axiosLike = caught as {
          message?: string;
          response?: { status?: number };
        };

        setError({
          message: String(
            typeof mapped === "string"
              ? mapped
              : mapped?.message ??
                  mapped?.title ??
                  axiosLike.message ??
                  "Unexpected error"
          ),
          status: axiosLike.response?.status ?? 500,
        });
      } finally {
        if (active) setLoadingLists(false);
      }
    };

    void loadClassLists();
    return () => {
      active = false;
    };
  }, [i18n.language, t, yearId, years]);

  const selectedYear = useMemo<SchoolYear | null>(() => {
    if (!yearId) return null;

    return (
      years.find(
        (year) =>
          !year.isDivider &&
          year.__rawId !== null &&
          year.__rawId !== undefined &&
          String(year.__rawId) === yearId
      ) ??
      years.find(
        (year) => !year.isDivider && year.__label === yearId
      ) ??
      null
    );
  }, [yearId, years]);

  const selectedYearLabel = selectedYear
    ? selectedYear.__label || buildYearLabel(selectedYear)
    : "—";

  const schoolYearOptions = useMemo(
    () =>
      years
        .filter((year) => !year.isDivider)
        .map((year) => {
          const id =
            year.__rawId !== null && year.__rawId !== undefined
              ? String(year.__rawId)
              : buildYearLabel(year);
          const label = year.__label || buildYearLabel(year);

          return {
            ...year,
            id,
            name: label,
            label,
          };
        }),
    [years]
  );


  if (!authenticated) {
    return (
      <main className="px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1600px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-12 text-center text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            {t("reports.auth_required", {
              defaultValue: "You must be signed in to view this report.",
            })}
          </div>
        </div>
      </main>
    );
  }

  if (!canView) {
    return (
      <main className="px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1600px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-12 text-center text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            {t("reports.no_access", {
              defaultValue: "You do not have access to this report.",
            })}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="lds-report-enter px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
      <style>{`
        .print-only { display: none; }
        .print-address { font-size: 11px; font-weight: 400; opacity: .75; }

        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-container { padding: 0 !important; box-shadow: none !important; }
          .print-root.per-class .per-class-only { display: block !important; }
          .print-root.per-class .compact-only { display: none !important; }
          .print-root.compact .per-class-only { display: none !important; }
          .print-root.compact .compact-only { display: block !important; }

          /*
           * The app shell around the page is a flex layout; Firefox ignores page breaks inside flex items
           * and stretches them over empty pages. Print the per-class report in plain block flow.
           */
          *:has(.print-root.per-class) {
            display: block !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }

          /* Each class on its own page: a break before every class but the first, so no blank page at the end. */
          .print-root.per-class .per-class-only > * + * { margin-top: 0 !important; }
          .print-root.per-class .report-section {
            display: block;
            break-before: page;
            page-break-before: always;
          }
          .print-root.per-class .report-section:first-child {
            break-before: auto;
            page-break-before: auto;
          }

          /* Clipping and scroll containers keep a long class list from flowing onto a next page. */
          .print-root.per-class .report-section,
          .print-root.per-class .report-section * {
            overflow: visible !important;
          }

          /* The class header stays with its table; a long class repeats the table head and never splits a row. */
          .print-root.per-class .report-section header {
            break-inside: avoid;
            break-after: avoid;
            page-break-after: avoid;
          }
          .print-root.per-class .report-section thead { display: table-header-group; }
          .print-root.per-class .report-section tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }

        @page {
          size: A4 landscape;
          margin: 10mm;
        }

        .compact-container { color: #111827; font-size: 10px; line-height: 1.2; }
        .compact-head, .compact-row {
          display: grid;
          grid-template-columns: 1fr 1.4fr 1.4fr 1.4fr;
          column-gap: 10px;
          padding: 2px 0;
        }
        .compact-head {
          margin-bottom: 4px;
          border-bottom: 1px solid rgba(0,0,0,.25);
          font-weight: 700;
        }
        .compact-row + .compact-row {
          border-top: 1px dashed rgba(0,0,0,.06);
        }
        .compact-class-label {
          margin: 8px 0 2px;
          color: #111827;
          font-weight: 700;
        }
        .muted { opacity: .75; font-weight: 400; }
        .nowrap { white-space: nowrap; }
      `}</style>

      <div className="space-y-6">
        <section className="no-print relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-6 py-7 shadow-sm dark:border-indigo-900 dark:from-indigo-950/50 dark:via-slate-950 dark:to-sky-950/40 sm:px-8">
          <FaUsers
            aria-hidden="true"
            className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-indigo-200 dark:text-indigo-900/40 sm:block"
          />

          <div className="relative z-10 flex min-w-0 items-center gap-5">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
              <FaUsers aria-hidden="true" />
            </div>

            <div className="min-w-0 max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                {t("reports.title", { defaultValue: "Reports" })}
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
                {t("reports.students.title", {
                  defaultValue: "Student reports",
                })}
              </h1>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                {t("reports.students.description", {
                  defaultValue:
                    "View and print class lists with student and parent contact details.",
                })}
              </p>
            </div>
          </div>
        </section>

        <section className="no-print overflow-visible rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-col gap-5 border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between lg:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                <FaCalendarAlt className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-base font-bold text-slate-950 dark:text-white">
                  {t("reports.students.options_title", { defaultValue: "Report options" })}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t("reports.students.options_description", {
                    defaultValue: "Select a school year and choose how the class lists should be printed.",
                  })}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 px-5 py-5 lg:flex-row lg:items-end lg:justify-between lg:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-[240px]">
                <SchoolYearDropdown
                  schoolYears={schoolYearOptions}
                  selectedSchoolYearId={yearId ?? ""}
                  onChange={(value) => setYearId(String(value || "") || null)}
                  label={t("reports.school_year", {
                    defaultValue: "School year",
                  })}
                />
              </div>
              {loadingLists && !loadingYears ? (
                <div className="inline-flex h-11 items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Spinner />
                  {t("reports.refreshing", { defaultValue: "Refreshing…" })}
                </div>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" onClick={() => triggerPrint("per-class")} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-indigo-600 dark:hover:text-indigo-300 dark:focus:ring-indigo-950">
                <FaPrint aria-hidden="true" />
                {t("reports.print_per_class", { defaultValue: "Print per class" })}
              </button>
              <button type="button" onClick={() => triggerPrint("compact")} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-indigo-950">
                <FaPrint aria-hidden="true" />
                {t("reports.print_compact", { defaultValue: "Print compact" })}
              </button>
            </div>
          </div>
          {isSafariBrowser() ? (
            <div className="-mt-2 flex px-5 pb-5 sm:justify-end lg:px-6">
              <LdsBadge tone="warning">
                {t("reports.print_safari_landscape_hint", {
                  defaultValue: "Safari: choose 'Landscape' in the print dialog (Show details → Orientation).",
                })}
              </LdsBadge>
            </div>
          ) : null}
        </section>

        {loadingYears ? (
          <div className="flex min-h-[180px] items-center justify-center rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
              <Spinner />
              {t("reports.loading", {
                defaultValue: "Loading report…",
              })}
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="rounded-[20px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            <span className="font-semibold">
              {t("reports.error", {
                defaultValue: "Unable to load report.",
              })}
            </span>{" "}
            {error.message}
          </div>
        ) : null}

        {!loadingYears && !error ? (
          <div
            className={`print-root ${
              printMode === "compact" ? "compact" : "per-class"
            }`}
          >
            <div className="per-class-only print-container space-y-6">
              {!loadingLists && classlists.length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  {t("reports.no_classes", {
                    defaultValue:
                      "No classes are available for this school year.",
                  })}
                </div>
              ) : null}

              {classlists.map((classroom, classroomIndex) => {
                const students = normalizeList<Student>(
                  classroom.children
                )
                  .slice()
                  .sort((a, b) =>
                    fullName(a).localeCompare(
                      fullName(b),
                      i18n.language || "en"
                    )
                  );

                const rows: StudentTableRow[] = students.map(
                  (student, studentIndex) => {
                    const { parent1, parent2 } =
                      extractParents(student);

                    return {
                      id: getEntityKey(
                        student,
                        `${classroomIndex}-${studentIndex}`
                      ),
                      child: student,
                      parent1,
                      parent2,
                      addresses: printAddresses(parent1, parent2),
                    };
                  }
                );

                const emptyMessage = t("reports.empty_class", {
                  defaultValue: "Empty class",
                });

                return (
                  <section
                    key={getEntityKey(
                      classroom,
                      `classroom-${classroomIndex}`
                    )}
                    className="report-section"
                  >
                    <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                      <header className="flex flex-col gap-3 border-b border-slate-200 px-5 py-5 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                            {classroomLabel(classroom)}
                          </h2>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {t("reports.school_year", {
                              defaultValue: "School year",
                            })}
                            : {selectedYearLabel}
                            {" · "}
                            {t("reports.teacher", {
                              defaultValue: "Teacher",
                            })}
                            :{" "}
                            {classroom.teacher ||
                              t("reports.no_teacher", {
                                defaultValue: "No teacher",
                              })}
                          </p>
                        </div>

                        <span className="inline-flex w-fit items-center rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">
                          {rows.length}{" "}
                          {t("reports.students.count", {
                            defaultValue: "Students",
                          }).toLowerCase()}
                        </span>
                      </header>

                      <LdsTableCard className="rounded-none border-0 shadow-none">
                        <LdsTableScroll>
                          <LdsTable className="min-w-[900px]">
                            <LdsTableHead>
                              <LdsTableRow>
                                <LdsTableHeadCell>{t("reports.child_name", { defaultValue: "Student" })}</LdsTableHeadCell>
                                <LdsTableHeadCell>{t("reports.parent1_name", { defaultValue: "Parent 1" })}</LdsTableHeadCell>
                                <LdsTableHeadCell>{t("reports.parent1_phone", { defaultValue: "Phone" })}</LdsTableHeadCell>
                                <LdsTableHeadCell>{t("reports.parent2_name", { defaultValue: "Parent 2" })}</LdsTableHeadCell>
                                <LdsTableHeadCell>{t("reports.parent2_phone", { defaultValue: "Phone" })}</LdsTableHeadCell>
                              </LdsTableRow>
                            </LdsTableHead>
                            <LdsTableBody>
                              {rows.length === 0 ? (
                                <LdsTableRow>
                                  <LdsTableCell colSpan={5} className="py-10 text-center text-slate-500 dark:text-slate-400">
                                    {emptyMessage}
                                  </LdsTableCell>
                                </LdsTableRow>
                              ) : (
                                rows.map((row) => (
                                  <LdsTableRow key={row.id}>
                                    <LdsTableCell className="font-medium text-slate-950 dark:text-white">
                                      {renderName(row.child)}
                                      {renderPrintAddress(row.addresses.child)}
                                    </LdsTableCell>

                                    <LdsTableCell>
                                      {renderName(row.parent1)}
                                      {renderPrintAddress(row.addresses.parent1)}
                                    </LdsTableCell>

                                    <LdsTableCell className="whitespace-nowrap">
                                      {phoneOf(row.parent1) ? (
                                        <a
                                          href={`tel:${phoneOf(row.parent1)}`}
                                          className="inline-flex items-center gap-2 hover:underline"
                                        >
                                          {phoneOf(row.parent1)}
                                          <FaPhone className="h-3.5 w-3.5" aria-hidden="true" />
                                        </a>
                                      ) : (
                                        "—"
                                      )}
                                    </LdsTableCell>

                                    <LdsTableCell>
                                      {renderName(row.parent2)}
                                      {renderPrintAddress(row.addresses.parent2)}
                                    </LdsTableCell>

                                    <LdsTableCell className="whitespace-nowrap">
                                      {phoneOf(row.parent2) ? (
                                        <a
                                          href={`tel:${phoneOf(row.parent2)}`}
                                          className="inline-flex items-center gap-2 hover:underline"
                                        >
                                          {phoneOf(row.parent2)}
                                          <FaPhone className="h-3.5 w-3.5" aria-hidden="true" />
                                        </a>
                                      ) : (
                                        "—"
                                      )}
                                    </LdsTableCell>
                                  </LdsTableRow>
                                ))
                              )}
                            </LdsTableBody>
                          </LdsTable>
                        </LdsTableScroll>
                      </LdsTableCard>
                    </div>
                  </section>
                );
              })}
            </div>

            <div className="compact-only hidden print-container">
              <div className="compact-container">
                <div className="muted" style={{ marginBottom: 4 }}>
                  {t("reports.school_year", {
                    defaultValue: "School year",
                  })}
                  : {selectedYearLabel}
                </div>

                <div className="compact-head">
                  <div className="nowrap">
                    {t("classrooms.class", {
                      defaultValue: "Class",
                    })}
                  </div>
                  <div className="nowrap">
                    {t("reports.child_name", {
                      defaultValue: "Student",
                    })}
                  </div>
                  <div className="nowrap">
                    {t("reports.parent1_name", {
                      defaultValue: "Parent 1",
                    })}
                  </div>
                  <div className="nowrap">
                    {t("reports.parent2_name", {
                      defaultValue: "Parent 2",
                    })}
                  </div>
                </div>

                {classlists.map((classroom, classroomIndex) => {
                  const students = normalizeList<Student>(
                    classroom.children
                  )
                    .slice()
                    .sort((a, b) =>
                      [a.lastName, a.firstName]
                        .filter(Boolean)
                        .join(" ")
                        .localeCompare(
                          [b.lastName, b.firstName]
                            .filter(Boolean)
                            .join(" "),
                          i18n.language || "en"
                        )
                    );

                  const name = classroomLabel(classroom) || "—";
                  const teacher = classroom.teacher
                    ? ` · ${t("reports.teacher", {
                        defaultValue: "Teacher",
                      })}: ${classroom.teacher}`
                    : "";

                  return (
                    <Fragment
                      key={getEntityKey(
                        classroom,
                        `compact-${classroomIndex}`
                      )}
                    >
                      <div className="compact-class-label">
                        {name}
                        {teacher}
                      </div>

                      {students.length === 0 ? (
                        <div className="compact-row muted">
                          <div>{name}</div>
                          <div>
                            {t("reports.empty_class", {
                              defaultValue: "Empty class",
                            })}
                          </div>
                          <div>—</div>
                          <div>—</div>
                        </div>
                      ) : (
                        students.map((student, studentIndex) => {
                          const { parent1, parent2 } =
                            extractParents(student);
                          const parent1Phone = phoneOf(parent1);
                          const parent2Phone = phoneOf(parent2);
                          const addresses = printAddresses(parent1, parent2);

                          return (
                            <div
                              className="compact-row"
                              key={getEntityKey(
                                student,
                                `${classroomIndex}-${studentIndex}`
                              )}
                            >
                              <div className="nowrap">{name}</div>
                              <div>
                                <div className="nowrap">
                                  {fullName(student) || "—"}
                                </div>
                                {addresses.child ? (
                                  <div className="muted">{addresses.child}</div>
                                ) : null}
                              </div>
                              <div>
                                <div className="nowrap">
                                  {fullName(parent1) || "—"}
                                </div>
                                {parent1Phone ? (
                                  <div className="muted nowrap">
                                    {parent1Phone}
                                  </div>
                                ) : null}
                                {addresses.parent1 ? (
                                  <div className="muted">{addresses.parent1}</div>
                                ) : null}
                              </div>
                              <div>
                                <div className="nowrap">
                                  {fullName(parent2) || "—"}
                                </div>
                                {parent2Phone ? (
                                  <div className="muted nowrap">
                                    {parent2Phone}
                                  </div>
                                ) : null}
                                {addresses.parent2 ? (
                                  <div className="muted">{addresses.parent2}</div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      </div>
    </main>
  );
}
